import type { EvidenceDTO, RunDTO, RunEventDTO, TaskDTO } from "@roomate/core";
import { runtimeLabel } from "./run-runtime.ts";

/**
 * Why a live note cannot reach this run, or null when it can (docs/specs/mission-control.md §4.5).
 * Until the run sources report capabilities (MC7) this is derived from the runtime: codex has no stdin channel.
 */
export function steerBlockedReason(run: Pick<RunDTO, "runtime" | "status">): string | null {
  if (run.status !== "running" && run.status !== "starting") return "This run has finished, so there is nothing left to steer.";
  if (run.runtime === "codex") return "Codex has no way to take a note while it runs, so this run can't be steered. Stop it and start a new one instead.";
  return null;
}

/** What the stats tile shows under "node": the node, or the runtime for a hub-driven run that has none. */
export function runWhere(run: Pick<RunDTO, "nodeId" | "runtime" | "host">): { label: "node" | "runtime"; value: string } {
  if (run.nodeId) return { label: "node", value: run.nodeId };
  return { label: "runtime", value: runtimeLabel(run) ?? run.host };
}

/** The events as plain terminal-style text, one line each, in order. */
export function rawOutput(events: readonly Pick<RunEventDTO, "kind" | "tool" | "text">[]): string {
  return events
    .map((e) => (e.kind === "tool_use" ? `$ ${e.tool ?? "tool"} ${e.text}`.trimEnd() : e.kind === "tool_result" ? `  ← ${e.text}` : e.text))
    .join("\n");
}

/** The deliverable's reference: the evidence's own ref for non-code work, else the branch tip, else the branch. Null while nothing exists. */
export function deliverableRef(task: Pick<TaskDTO, "branch" | "mergedSha">, evidence: EvidenceDTO | undefined): string | null {
  const p = evidence?.payload;
  return p?.deliverable?.ref ?? task.mergedSha ?? p?.branchSha ?? task.branch ?? null;
}
